<template>
  <div class="f-je m-bx4">
    <donut-selector-prefabs-date-range :update-range="updateDateRange" :include-weekends="true" />
  </div>
  <donut-table
    :headers="headers"
    :data="tableData"
  >
    <template #redeemed="{ entry }"><div class="t-sm gray">{{ entry.redeemed_at }}</div></template>
    <template #user="{ entry }"><div class="t-sm gray">{{ entry.redeemer }}</div></template>
    <template #reward="{ entry }"><div class="t-sm gray">{{ entry.listing.name }}</div></template>
    <template #points="{ entry }"><div class="t-sm gray">{{ entry.listing.points_cost }}</div></template>
    <template #fulfilled="{ entry }"><div class="t-sm gray">{{ valueForEntry(entry, 'fulfilled_at') || 'Unfulfilled' }}</div></template>
    <template #action="{ entry }">
      <div v-if="valueForEntry(entry, 'canceled')" class="t-sm gray">
        Canceled
      </div>
      <div v-else-if="valueForEntry(entry, 'fulfilled_at')" class="t-sm gray">
        Fulfilled {{ valueForEntry(entry, 'fulfiller') ? 'by ' + valueForEntry(entry, 'fulfiller') : 'automatically' }}
      </div>
      <button v-else :class="`inv ${activelyFulfilling[entry.id] ? 'loading' : ''}`" @click="fulfill(entry)">Mark Fulfilled</button>
    </template>
  </donut-table>
  <div v-if="isFetching" class="f-jc m-bx2"><donut-loading-dots /></div>
  <div v-else-if="!tableData.length" class="f-jc m-vx2">
    <div class="t">Nobody has redeemed any {{ $i18n.t('shoutouts.reward', { count: 2 }) }} yet!</div>
    <div v-if="!dateRangeIsAllTime" class="t">&nbsp;Try adjusting the date window to see more.</div>
  </div>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import useInfiniteScroll from 'composables/infinite-scroll';
  import Network from 'lib/api/network';
  import { capitalize } from 'lodash';

  const $i18n = inject('$i18n');
  const { state, getters, dispatch } = useStore();

  const headers = [
    { label: 'Date Redeemed', key: 'redeemed' },
    { label: 'User', key: 'user' },
    { label: capitalize($i18n.t('shoutouts.reward', { count: 1 })), key: 'reward' },
    { label: state.shoutoutsProgram.program.points_name_plural, key: 'points' },
    { label: 'Date Fulfilled', key: 'fulfilled' },
    { key: 'action' },
  ];

  // Current Redemptions Collection
  const collection = computed(() => getters['rewardsRedemptions/currentRedemptionCollection']);
  const tableData = computed(() => collection.value?.list || []);

  // Data Fetching
  const nextPage = computed(() => (collection.value ? collection.value.nextPage : 1));
  const isFetching = computed(() => state.rewardsRedemptions.isFetching);
  const fetchData = () => dispatch('rewardsRedemptions/fetchRedemptions', { page: nextPage.value });
  const shouldFetchAtEndOfScroll = computed(() => nextPage.value && !isFetching.value);
  const { fetchFirstPage } = useInfiniteScroll({ fetchData, shouldFetchAtEndOfScroll });

  // Date Window Selector
  const updateDateRange = ({ startDate, endDate }) => {
    dispatch('rewardsRedemptions/updateDateRange', { startDate, endDate, fetchFirstPage });
  };
  const dateRangeIsAllTime = computed(() => !(state.rewardsRedemptions.startDate || state.rewardsRedemptions.endDate));

  // Fulfillment
  const activelyFulfilling = ref({});
  const fulfill = (entry) => {
    activelyFulfilling.value[entry.id] = true;
    dispatch('rewardsRedemptions/fulfill', { redemptionId: entry.id })
      .then(() => {
        activelyFulfilling.value[entry.id] = false;
      });
  };
  const valueForEntry = (entry, key) => {
    const asyncValues = state.rewardsRedemptions.asyncValuesByRedemption;
    return asyncValues[entry.id]?.[key] || entry[key];
  };
</script>
